// The card of a slide change the deck chat was asked for (Lane 1355, slice 2: add a
// slide; Lane 1370: reword a slide's text, remove a slide).
//
// The server prepares the change in the background; this card follows it (a poll
// while it is being prepared), shows it (the prepared page; for a reword the old and
// new text side by side; for a remove the slide), and offers its button and Prepare
// again. The change runs through the deck page's own save queue (`applyChange`), so
// it never races an edit or a save; the deck reloads afterwards. The card never
// changes the deck itself.
import React, { useEffect, useRef, useState } from 'react';
import { newKey } from '../../utils/clientKey';

const POLL_MS = 2500;
const PREVIEW_WIDTH = 300;           // the page is 1920 x 1080
const UNKNOWN_PERIOD = 'Reporting period not supplied';

// What each kind of change says (Lane 1370).
const WORDS = {
  add: { button: 'Add slide', unsaved: 'Save your edits first, then add the slide.',
    applied: 'Slide added to the deck.', undone: 'Slide added, then undone.',
    failed: 'The slide could not be prepared.' },
  reword: { button: 'Apply change', unsaved: 'Save your edits first, then apply the change.',
    applied: 'Slide text changed.', undone: 'Slide text changed, then undone.',
    failed: 'The new wording could not be prepared.' },
  remove: { button: 'Remove slide', unsaved: 'Save your edits first, then remove the slide.',
    applied: 'Slide removed from the deck.', undone: 'Slide removed, then undone.',
    failed: 'The slide could not be removed.' },
};

function preparingLine(mode, where, slide) {
  if (mode === 'reword') return `Checking the new wording for slide ${slide}…`;
  if (mode === 'remove') return `Preparing to remove slide ${slide}…`;
  return `Preparing a new slide ${where}…`;
}

// A reword's old and new text, one row per text unit.
function Wording({ units }) {
  return (
    <table className="dk-chat-wording">
      <thead><tr><th scope="col">Now</th><th scope="col">New</th></tr></thead>
      <tbody>
        {units.map((u, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={i}>
            <td><span className="dk-chat-kind">{u.kind}</span>{u.before}</td>
            <td><span className="dk-chat-kind">{u.kind}</span>{u.after}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Mount with key={attemptId}: a new attempt never shows the previous page.
// `onShown(attemptId)` once this attempt's page is on screen: only then can it be
// added (by the button or a typed yes; Codex s2 r5 #4).
function Preview({ api, proposalId, attemptId, onShown, title }) {
  const [html, setHtml] = useState(null);
  const shownRef = useRef(onShown);
  shownRef.current = onShown;
  // Shown while this preview is mounted and loaded; hidden again when it unmounts
  // or fails (Codex s2 r6 #5).
  useEffect(() => {
    let live = true;
    api.preview(proposalId, attemptId).then((r) => {
      if (!live) return;
      setHtml(r.html || '');
      // Shown once the frame has drawn it (onLoad below), never before (Lane 1370,
      // Codex p2 r1).
      if (!r.html) shownRef.current(attemptId, false);
    }).catch(() => { if (live) { setHtml(''); shownRef.current(attemptId, false); } });
    return () => { live = false; shownRef.current(attemptId, false); };
  }, [api, proposalId, attemptId]);
  if (html === null) return <div className="wchat-mut">Loading the preview…</div>;
  if (!html) return <div className="wchat-mut">The preview could not load.</div>;
  const scale = PREVIEW_WIDTH / 1920;
  return (
    <div className="dk-chat-preview" style={{ width: PREVIEW_WIDTH, height: Math.round(1080 * scale) }}>
      {/* No scripts, no same-origin access: the page is shown, never run. */}
      <iframe title={title || 'Prepared slide'} sandbox="" srcDoc={html}
        onLoad={() => shownRef.current(attemptId, true)}
        style={{ width: 1920, height: 1080, transform: `scale(${scale})` }} />
    </div>
  );
}

export default function DeckChatProposalCard({ card, view, api, applyChange, onView,
  getDeckContext, busy, shown, onShown }) {
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const keyRef = useRef(null);
  const attempt = view && view.attempt;
  const preparing = !view || (attempt && (attempt.status === 'queued' || attempt.status === 'preparing'));

  // Follow the slide while it is being prepared; a failed look tries again.
  useEffect(() => {
    if (card.state !== 'preparing' || !card.proposal_id) return undefined;
    if (view && !preparing) return undefined;
    let live = true;
    const t = setTimeout(() => {
      api.proposal(card.proposal_id).then((v) => { if (live) onView(card.proposal_id, v); })
        .catch(() => { if (live) setPollTick((n) => n + 1); });
    }, view || pollTick ? POLL_MS : 0);
    return () => { live = false; clearTimeout(t); };
  }, [api, card, view, preparing, onView, pollTick]);

  if (card.state === 'refused') {
    return <div className="dk-chat-card wchat-notice">{card.message}</div>;
  }
  const mode = (view && view.mode) || card.mode || 'add';
  const words = WORDS[mode] || WORDS.add;
  if (card.state === 'applied') {
    return <div className="dk-chat-card wchat-notice">{words.applied}</div>;
  }
  const where = `after slide ${(view && view.position) || card.position}`;
  // The slide a reword or remove is about: the attempt's own place once prepared.
  const slide = (attempt && attempt.position) || (view && view.position) || card.position;
  const title = (attempt && attempt.title) || (view && view.title) || card.anchor_headline || '';
  const period = (view && view.period) || card.period;
  const periodLine = period && period !== UNKNOWN_PERIOD ? ` · ${period}` : '';

  const add = async () => {
    setError(''); setWorking(true);
    try {
      const out = await applyChange(async (clean) => {
        if (!clean) return { refused: words.unsaved };
        const r = await api.confirm(card.proposal_id, attempt.attempt_id, attempt.page_digest);
        return { receipt: r, reload: true, position: r.position, editRevision: r.edit_revision };
      });
      if (out && out.refused) setError(out.refused);
      onView(card.proposal_id, await api.proposal(card.proposal_id));
    } catch (e) {
      setError(e.message);
      // The reply may have been lost after the slide was added: if so, reload the
      // deck now (Codex s2 r5 #3).
      try {
        const v = await api.proposal(card.proposal_id);
        onView(card.proposal_id, v);
        if (v && v.operation && !v.operation.undone) {
          setError('');
          // The deck page reloads it, or says what to do when there are unsaved edits.
          await applyChange(async () => ({ reload: true, position: v.operation.position,
            editRevision: v.operation.edit_revision }));
        }
      } catch (ignored) { /* the card keeps its error */ }
    } finally {
      setWorking(false);
    }
  };

  const again = async () => {
    setError(''); setWorking(true);
    if (!keyRef.current) keyRef.current = newKey();   // one key per press sequence
    try {
      const ctx = getDeckContext();
      if (!ctx.may_request) {
        setError('Save your edits first, then press Prepare again.');
        return;
      }
      const made = await api.prepareAgain(card.proposal_id, {
        client_key: keyRef.current, edit_revision: ctx.edit_revision,
        source_revision: ctx.source_revision, selected_position: ctx.selected_position,
      });
      // Show the new attempt at once, so the card follows it (and cannot be pressed
      // again) even if the next read fails (Codex s2 r5 #5).
      onView(card.proposal_id, { ...view, attempt: { attempt_id: made.attempt_id,
        status: 'queued', stale: false } });
      keyRef.current = null;
      api.proposal(card.proposal_id).then((v) => onView(card.proposal_id, v)).catch(() => {});
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  let body;
  if (view && view.operation) {
    body = (
      <div className="wchat-mut">
        {view.operation.undone ? words.undone : words.applied}
      </div>
    );
  } else if (view && view.status === 'replaced') {
    body = <div className="wchat-mut">Replaced by a newer request.</div>;
  } else if (preparing) {
    body = <div className="wchat-mut" aria-live="polite">{preparingLine(mode, where, slide)}</div>;
  } else if (attempt && attempt.status === 'ready' && !attempt.stale && mode !== 'add') {
    body = (
      <>
        <div className="wchat-mut dk-chat-title" title={title || undefined}>
          {mode === 'remove' ? 'Remove slide' : 'New wording for slide'} {slide}
          {title ? `: ${title}` : ''}
        </div>
        {mode === 'reword' && <Wording units={attempt.units || []} />}
        <Preview key={attempt.attempt_id} api={api} proposalId={card.proposal_id}
          attemptId={attempt.attempt_id} onShown={onShown}
          title={mode === 'remove' ? 'Slide to remove' : 'Slide with the new wording'} />
        <div className="wchat-row">
          <button type="button" className="wchat-btn wchat-btn--primary"
            disabled={working || busy || !shown[attempt.attempt_id]}
            onClick={add}>{words.button}</button>
          {mode === 'reword' && (
            <button type="button" className="wchat-btn" disabled={working || busy}
              onClick={again}>Prepare again</button>
          )}
        </div>
      </>
    );
  } else if (attempt && attempt.status === 'ready' && !attempt.stale) {
    body = (
      <>
        <div className="wchat-mut">New slide {where}{periodLine}</div>
        <Preview key={attempt.attempt_id} api={api} proposalId={card.proposal_id}
          attemptId={attempt.attempt_id} onShown={onShown} />
        <div className="wchat-row">
          <button type="button" className="wchat-btn wchat-btn--primary"
            disabled={working || busy || !shown[attempt.attempt_id]}
            onClick={add}>Add slide</button>
          <button type="button" className="wchat-btn" disabled={working || busy}
            onClick={again}>Prepare again</button>
        </div>
      </>
    );
  } else {
    const reason = attempt && attempt.status === 'ready'
      ? 'The deck changed after this was prepared.'
      : (attempt && attempt.reason) || words.failed;
    body = (
      <>
        <div className="wchat-mut">{reason}</div>
        <div className="wchat-row">
          <button type="button" className="wchat-btn" disabled={working || busy}
            onClick={again}>Prepare again</button>
        </div>
      </>
    );
  }
  return (
    <div className="dk-chat-card" data-testid="deck-chat-card">
      {body}
      {error && <div className="wchat-error" role="alert">{error}</div>}
    </div>
  );
}
